import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useRepository } from '../../data/RepositoryProvider';
import { MutationError } from '../partners/PartnerFormDialog';
import { withdrawalControlsRepositoryToken } from './repository';
import { formatControlMoney, type PartnerWithdrawalPolicy } from './types';

type PolicyMode = 'create' | 'edit' | 'status';

interface PolicyForm {
  readonly currencyCode: string;
  readonly minimumAmount: string;
  readonly maximumAmount: string;
  readonly reason: string;
}

const decimalPattern = /^(0|[1-9][0-9]{0,13})(?:\.[0-9]{1,6})?$/u;

export function WithdrawalPolicyDialog({
  mode,
  onClose,
  onSaved,
  open,
  partnerId,
  policy,
}: {
  readonly mode: PolicyMode;
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly open: boolean;
  readonly partnerId: string;
  readonly policy?: PartnerWithdrawalPolicy | undefined;
}) {
  const repository = useRepository(withdrawalControlsRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const [form, setForm] = useState<PolicyForm>(() => defaults(policy));
  const activating = mode === 'status' && policy?.status === 'SUSPENDED';

  useEffect(() => {
    if (!open) return;
    setForm(defaults(policy));
    setBusy(false);
    setConfirmed(false);
    setError(null);
    guard.current = false;
  }, [open, policy]);

  const update = (field: keyof PolicyForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError(null);
  };

  const submit = async () => {
    if (guard.current || !valid(form, mode) || (activating && !confirmed)) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'create') {
        await repository.createPolicy(partnerId, {
          currencyCode: form.currencyCode.trim().toUpperCase(),
          minimumAmount: form.minimumAmount.trim(),
          maximumAmount: form.maximumAmount.trim(),
          reason: form.reason.trim(),
        });
      } else if (mode === 'edit' && policy !== undefined) {
        await repository.updatePolicy(partnerId, policy.id, {
          minimumAmount: form.minimumAmount.trim(),
          maximumAmount: form.maximumAmount.trim(),
          reason: form.reason.trim(),
        });
      } else if (policy !== undefined) {
        await repository.changePolicyStatus(partnerId, policy.id, {
          status: policy.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
          reason: form.reason.trim(),
        });
      }
      onSaved();
      onClose();
    } catch (cause) {
      setError(safeMutationError(cause, t('withdrawalControls.errors.mutation')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  };

  const title =
    mode === 'create'
      ? t('withdrawalControls.policy.createTitle')
      : mode === 'edit'
        ? t('withdrawalControls.policy.editTitle')
        : activating
          ? t('withdrawalControls.policy.activateTitle')
          : t('withdrawalControls.policy.suspendTitle');

  return (
    <Dialog
      aria-describedby="withdrawal-policy-description"
      fullWidth
      maxWidth="sm"
      onClose={() => !guard.current && onClose()}
      open={open}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography id="withdrawal-policy-description" color="text.secondary">
            {t('withdrawalControls.policy.description')}
          </Typography>
          {error === null ? null : <MutationError error={error} />}
          {mode === 'status' && policy !== undefined ? (
            <>
              <Paper component="section" variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography component="h3" variant="h6">
                    {t('withdrawalControls.policy.impact')}
                  </Typography>
                  <Typography>{`${formatControlMoney(policy.minimumAmount, policy.currencyCode)} \u2013 ${formatControlMoney(policy.maximumAmount, policy.currencyCode)}`}</Typography>
                  <Typography>{t('withdrawalControls.policy.dailyLimitDisabled')}</Typography>
                </Stack>
              </Paper>
              {activating ? (
                <>
                  <Paper component="aside" variant="outlined" sx={{ p: 2 }}>
                    <Typography component="h3" variant="subtitle2">
                      {t('withdrawalControls.policy.platformBehaviorTitle')}
                    </Typography>
                    <Typography variant="body2">
                      {t('withdrawalControls.policy.synchronousDelivery')}
                    </Typography>
                  </Paper>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmed}
                        disabled={busy}
                        onChange={(event) => setConfirmed(event.target.checked)}
                      />
                    }
                    label={t('withdrawalControls.policy.confirmActivation')}
                  />
                </>
              ) : (
                <Alert severity="warning">{t('withdrawalControls.policy.suspendImpact')}</Alert>
              )}
            </>
          ) : (
            <>
              <TextField
                disabled={busy || mode === 'edit'}
                error={!/^[A-Za-z]{3}$/u.test(form.currencyCode)}
                label={t('withdrawalControls.fields.currency')}
                onChange={(event) => update('currencyCode', event.target.value)}
                required
                value={form.currencyCode}
              />
              <TextField
                disabled={busy}
                error={!decimalPattern.test(form.minimumAmount)}
                label={t('withdrawalControls.fields.minimumAmount')}
                onChange={(event) => update('minimumAmount', event.target.value)}
                required
                value={form.minimumAmount}
              />
              <TextField
                disabled={busy}
                error={!decimalPattern.test(form.maximumAmount)}
                label={t('withdrawalControls.fields.maximumAmount')}
                onChange={(event) => update('maximumAmount', event.target.value)}
                required
                value={form.maximumAmount}
              />
            </>
          )}
          <TextField
            disabled={busy}
            error={form.reason.length > 2000}
            label={t('withdrawalControls.fields.reason')}
            minRows={2}
            multiline
            onChange={(event) => update('reason', event.target.value)}
            required
            slotProps={{ htmlInput: { maxLength: 2000 } }}
            value={form.reason}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button
          disabled={!valid(form, mode) || busy || (activating && !confirmed)}
          loading={busy}
          onClick={() => void submit()}
          variant="contained"
        >
          {mode === 'create'
            ? t('withdrawalControls.actions.createPolicy')
            : mode === 'edit'
              ? t('actions.save')
              : activating
                ? t('withdrawalControls.actions.activatePolicy')
                : t('withdrawalControls.actions.suspendPolicy')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function defaults(policy?: PartnerWithdrawalPolicy): PolicyForm {
  return {
    currencyCode: policy?.currencyCode ?? 'THB',
    minimumAmount: policy?.minimumAmount ?? '500.00',
    maximumAmount: policy?.maximumAmount ?? '100000.00',
    reason: '',
  };
}

function valid(form: PolicyForm, mode: PolicyMode): boolean {
  if (form.reason.trim().length === 0 || form.reason.trim().length > 2000) return false;
  if (mode === 'status') return true;
  return (
    /^[A-Za-z]{3}$/u.test(form.currencyCode) &&
    decimalPattern.test(form.minimumAmount) &&
    decimalPattern.test(form.maximumAmount)
  );
}

function safeMutationError(
  cause: unknown,
  fallback: string,
): Pick<AppError, 'message' | 'requestId'> {
  const requestId =
    typeof cause === 'object' &&
    cause !== null &&
    'requestId' in cause &&
    typeof cause.requestId === 'string'
      ? cause.requestId
      : null;
  return { message: fallback, requestId };
}
